import { useMemo, useState } from "react";
import { capitalize, FCX, ID } from "@datassential/platform-ui-lib";
import cn from "classnames";
import { ConsumerKeyword } from "@models/ConsumerData";
import GroupsColumnsLineChart, { ChartValue } from "@components/GroupsColumnsLineChart/GroupsColumnsLineChart";
import CategoryProfileSubtitle from "@pages/CategoryProfile/components/CategoryProfileSubtitle/CategoryProfileSubtitle";
import { buildTrendChart } from "../../Consumer.helpers";
import "./ConsumerTrend.styles.scss";

interface Props {
    topKeywords: ConsumerKeyword[];
    topKeywordsByChange: ConsumerKeyword[];
    metricLabel: string;
    categoryName: string;
}

const formatPercent = (value: ChartValue, withSign = false) => {
    if (typeof value !== "number") return "-";
    const rounded = Math.round(value);
    return `${withSign && rounded > 0 ? "+" : ""}${rounded}%`;
};
const latestValue = (keyword: ConsumerKeyword) => keyword.trend[keyword.trend.length - 1]?.value ?? 0;

const ConsumerTrend: FCX<Props> = ({ topKeywords, topKeywordsByChange, metricLabel, categoryName, className }) => {
    const [showByChange, setShowByChange] = useState(true);
    const [highlightedSeriesId, setHighlightedSeriesId] = useState<ID>(null);

    const byMetricDescending = useMemo(
        () => [...topKeywords].sort((a, b) => latestValue(b) - latestValue(a)),
        [topKeywords],
    );

    const byChangeDescending = useMemo(
        () => [...topKeywordsByChange].sort((a, b) => b.twoYearChange - a.twoYearChange),
        [topKeywordsByChange],
    );

    const keywords = showByChange && byChangeDescending.length > 0
        ? byChangeDescending
        : byMetricDescending;

    const { series, groups, columns } = useMemo(() => buildTrendChart(keywords), [keywords]);

    const renderTooltipTitle = (groupTitle: string, columnTitle: string) => (
        <>
            {metricLabel.toUpperCase()}
            <br />
            <span className="ConsumerTrend__tooltip-period">{columnTitle} {groupTitle}</span>
        </>
    );

    return (
        <div className={cn("ConsumerTrend", className)}>
            <div className="ConsumerTrend__body">
                <CategoryProfileSubtitle className="ConsumerTrend__subtitle">
                    {`Historic Trend: ${categoryName}`}
                </CategoryProfileSubtitle>
                <div className="ConsumerTrend__chart">
                    <GroupsColumnsLineChart
                        animationKey={`${metricLabel}-${showByChange ? "change" : "metric"}`}
                        series={series}
                        groups={groups}
                        columns={columns}
                        activeSeriesId={highlightedSeriesId}
                        renderValue={formatPercent}
                        renderTooltipTitle={renderTooltipTitle}
                        testId="blockCategoryProfileConsumerChart"
                    />
                </div>
            </div>

            <div className="ConsumerTrend__legend" data-testid="blockCategoryProfileConsumerLegend">
                <div className="ConsumerTrend__legend-header">
                    <span className="ConsumerTrend__legend-name-col" />
                    <span
                        className={cn(
                            "ConsumerTrend__legend-col",
                            !showByChange && "ConsumerTrend__legend-col--active",
                        )}
                        onClick={() => setShowByChange(false)}
                    >
                        {metricLabel}
                    </span>
                    <span
                        className={cn(
                            "ConsumerTrend__legend-col",
                            showByChange && "ConsumerTrend__legend-col--active",
                        )}
                        onClick={() => setShowByChange(true)}
                        data-testid="buttonCategoryProfileConsumerTwoYearChange"
                    >
                        2 Yr Change
                    </span>
                </div>
                <ul className="ConsumerTrend__legend-list">
                    {keywords.map((keyword, index) => (
                        <li
                            key={keyword.id}
                            className={cn(
                                "ConsumerTrend__legend-item",
                                highlightedSeriesId !== null && highlightedSeriesId !== keyword.id && "is-dimmed",
                                highlightedSeriesId === keyword.id && "is-active",
                            )}
                            onMouseEnter={() => setHighlightedSeriesId(keyword.id)}
                            onMouseLeave={() => setHighlightedSeriesId(null)}
                        >
                            <div className="ConsumerTrend__legend-name">
                                <span
                                    className="ConsumerTrend__legend-swatch"
                                    style={{ backgroundColor: series[index]?.color }}
                                />
                                {capitalize(keyword.name)}
                            </div>
                            <span
                                className={cn(
                                    "ConsumerTrend__legend-cell",
                                    !showByChange && "ConsumerTrend__legend-cell--active",
                                )}
                            >
                                {formatPercent(latestValue(keyword))}
                            </span>
                            <span
                                className={cn(
                                    "ConsumerTrend__legend-cell",
                                    showByChange && "ConsumerTrend__legend-cell--active",
                                )}
                            >
                                {formatPercent(keyword.twoYearChange, true)}
                            </span>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
};

export default ConsumerTrend;
